/**
 * SERVICIO DE CONVERSACIONES PARA SUPABASE
 * 
 * Maneja todas las operaciones CRUD para conversaciones y mensajes
 * con integración completa a Supabase y manejo de errores robusto.
 */

import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { User, Conversation, Message } from '../types';

export interface SupabaseConversation {
  id: string;
  participant1_id: string;
  participant2_id: string;
  created_at: string;
  updated_at: string;
}

export interface SupabaseMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  timestamp: string;
  read: boolean;
}

export class ConversationsService {
  
  /**
   * Crea una nueva conversación entre dos usuarios
   */
  static async createConversation(
    user1Id: string, 
    user2Id: string
  ): Promise<{ success: boolean; conversation?: Conversation; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase no está configurado' };
    }

    try {
      console.log('💬 Creando conversación entre usuarios:', { user1Id, user2Id });

      // Verificar si ya existe una conversación entre estos usuarios
      const existingConversation = await this.findConversationBetweenUsers(user1Id, user2Id);
      if (existingConversation.success && existingConversation.conversation) {
        console.log('✅ Conversación existente encontrada');
        return existingConversation;
      }

      // Crear nueva conversación
      const conversationData = {
        id: `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        participant1_id: user1Id,
        participant2_id: user2Id
      };

      const { data, error } = await supabase
        .from('conversations')
        .insert([conversationData])
        .select()
        .single();

      if (error) {
        console.error('❌ Error al crear conversación:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Conversación creada exitosamente:', data.id);
      
      // Convertir a formato frontend
      const conversation: Conversation = {
        id: data.id,
        participants: [], // Se llenarían con los datos de usuarios
        messages: [],
        updatedAt: new Date(data.updated_at)
      };

      return { success: true, conversation };

    } catch (error) {
      console.error('❌ Error inesperado al crear conversación:', error);
      return { 
        success: false, 
        error: `Error de conexión: ${error instanceof Error ? error.message : 'Error desconocido'}` 
      };
    }
  }

  /**
   * Obtiene una conversación por ID desde Supabase
   */
  static async findConversationById(
    conversationId: string
  ): Promise<{ success: boolean; conversation?: SupabaseConversation; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase no está configurado' };
    }

    try {
      console.log('🔍 Buscando conversación por ID:', conversationId);

      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (error) {
        console.error('❌ Error al buscar conversación por ID:', error);
        return { success: false, error: error.message };
      }

      if (!data) {
        console.log('📭 Conversación no encontrada');
        return { success: true, conversation: undefined };
      }

      console.log('✅ Conversación encontrada:', data.id);
      return { success: true, conversation: data };

    } catch (error) {
      console.error('❌ Error inesperado al buscar conversación:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }

  /**
   * Busca una conversación existente entre dos usuarios
   * CORREGIDO: Sintaxis de consulta OR simplificada
   */
  static async findConversationBetweenUsers(
    user1Id: string, 
    user2Id: string
  ): Promise<{ success: boolean; conversation?: Conversation; error?: string }> {
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase no configurado - usando datos locales');
      return { success: true, conversation: undefined };
    }

    try {
      console.log('🔍 Buscando conversación entre usuarios:', { user1Id, user2Id });
      
      // Buscar conversación en ambas direcciones usando OR
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`and(participant1_id.eq.${user1Id},participant2_id.eq.${user2Id}),and(participant1_id.eq.${user2Id},participant2_id.eq.${user1Id})`);

      if (error) {
        console.error('❌ Error al buscar conversación:', error);
        return { success: false, error: error.message };
      }

      if (!data || data.length === 0) {
        console.log('📭 No se encontró conversación existente');
        return { success: true, conversation: undefined };
      }

      // Tomar la primera conversación si existen múltiples
      const conversationData = data[0];
      console.log('✅ Conversación existente encontrada:', conversationData.id);
      
      // Convertir a formato frontend
      const conversation: Conversation = {
        id: conversationData.id,
        participants: [], // Se llenarían con los datos de usuarios
        messages: [],
        updatedAt: new Date(conversationData.updated_at)
      };

      return { success: true, conversation };

    } catch (error) {
      console.error('❌ Error inesperado al buscar conversación:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }

  /**
   * Obtiene todas las conversaciones de un usuario
   * CORREGIDO: Consulta OR simplificada
   */
  static async getUserConversations(
    userId: string
  ): Promise<{ success: boolean; conversations?: any[]; error?: string }> {
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase no configurado - devolviendo array vacío');
      return { success: true, conversations: [] };
    }

    try {
      console.log('📋 Obteniendo conversaciones para usuario:', userId);

      // Usar consulta OR simplificada para buscar conversaciones
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('❌ Error al obtener conversaciones:', error);
        console.warn('⚠️ Fallback: Devolviendo array vacío de conversaciones');
        return { success: true, conversations: [] };
      }

      console.log(`✅ ${data?.length || 0} conversaciones encontradas`);

      // Agregar campos necesarios para el frontend
      const conversationsWithMetadata = (data || []).map(conv => ({
        ...conv,
        participant1_id: conv.participant1_id,
        participant2_id: conv.participant2_id
      }));

      return { success: true, conversations: conversationsWithMetadata };

    } catch (error) {
      console.error('❌ Error inesperado al obtener conversaciones:', error);
      // Fallback para mantener la app funcionando
      console.warn('⚠️ Fallback: Devolviendo array vacío por error de conexión');
      return { success: true, conversations: [] };
    }
  }

  /**
   * Obtiene un usuario por ID
   */
  static async getUserById(userId: string) {
    try {
      console.log('👤 Buscando usuario por ID:', userId);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ Error al buscar usuario:', error);
        return { success: false, error: error.message };
      }

      if (!data) {
        console.log('👤 Usuario no encontrado');
        return { success: false, error: 'Usuario no encontrado' };
      }

      console.log('✅ Usuario encontrado:', { id: data.id, name: data.name });
      return { success: true, user: data };
      
    } catch (error) {
      console.error('❌ Error inesperado al buscar usuario:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }

  /**
   * Envía un mensaje en una conversación
   */
  static async sendMessage(
    conversationId: string,
    senderId: string,
    receiverId: string,
    content: string
  ): Promise<{ success: boolean; message?: Message; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase no está configurado' };
    }

    // Validar contenido del mensaje
    if (!content || !content.trim()) {
      return { success: false, error: 'El mensaje no puede estar vacío' };
    }

    try {
      console.log('📤 Enviando mensaje:', { conversationId, senderId, content: content.substring(0, 50) + '...' });

      const messageData = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        conversation_id: conversationId,
        sender_id: senderId,
        receiver_id: receiverId,
        content: content.trim(),
        read: false
      };

      const { data, error } = await supabase
        .from('messages')
        .insert([messageData])
        .select()
        .single();

      if (error) {
        console.error('❌ Error al enviar mensaje:', error);
        return { success: false, error: error.message };
      }

      // Actualizar timestamp de la conversación
      await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);

      console.log('✅ Mensaje enviado exitosamente:', data.id);

      // Convertir a formato frontend
      const message: Message = {
        id: data.id,
        senderId: data.sender_id,
        receiverId: data.receiver_id,
        content: data.content,
        timestamp: new Date(data.timestamp),
        read: data.read
      };

      return { success: true, message };

    } catch (error) {
      console.error('❌ Error inesperado al enviar mensaje:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }

  /**
   * Obtiene mensajes de una conversación
   */
  static async getConversationMessages(
    conversationId: string,
    limit: number = 50
  ): Promise<{ success: boolean; messages?: Message[]; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase no está configurado' };
    }

    try {
      console.log('📨 Obteniendo mensajes para conversación:', conversationId);

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('timestamp', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('❌ Error al obtener mensajes:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ ${data?.length || 0} mensajes obtenidos`);

      // Convertir a formato frontend
      const messages: Message[] = data?.map(msg => ({
        id: msg.id,
        senderId: msg.sender_id,
        receiverId: msg.receiver_id,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
        read: msg.read
      })) || [];

      return { success: true, messages };

    } catch (error) {
      console.error('❌ Error inesperado al obtener mensajes:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }

  /**
   * Marca mensajes como leídos
   */
  static async markMessagesAsRead(
    conversationId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase no está configurado' };
    }

    try {
      console.log('👁️ Marcando mensajes como leídos:', { conversationId, userId });

      const { error } = await supabase
        .from('messages')
        .update({ read: true })
        .eq('conversation_id', conversationId)
        .eq('receiver_id', userId)
        .eq('read', false);

      if (error) {
        console.error('❌ Error al marcar mensajes como leídos:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Mensajes marcados como leídos');
      return { success: true };

    } catch (error) {
      console.error('❌ Error inesperado al marcar mensajes:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }

  /**
   * Elimina una conversación y todos sus mensajes
   */
  static async deleteConversation(
    conversationId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured()) {
      return { success: false, error: 'Supabase no está configurado' };
    }

    try {
      console.log('🗑️ Eliminando conversación:', conversationId);

      // Verificar que el usuario es participante de la conversación
      const { data: conversation, error: fetchError } = await supabase
        .from('conversations')
        .select('participant1_id, participant2_id')
        .eq('id', conversationId)
        .single();

      if (fetchError || !conversation) {
        return { success: false, error: 'Conversación no encontrada' };
      }

      if (conversation.participant1_id !== userId && conversation.participant2_id !== userId) {
        return { success: false, error: 'No tienes permisos para eliminar esta conversación' };
      }

      // Los mensajes se eliminarán automáticamente por CASCADE
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (error) {
        console.error('❌ Error al eliminar conversación:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Conversación eliminada exitosamente');
      return { success: true };

    } catch (error) {
      console.error('❌ Error inesperado al eliminar conversación:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }
}